import find from 'lodash/find';
import map from 'lodash/map';
import assign from 'lodash/assign';
import includes from 'lodash/includes';
import uiRouter from '@uirouter/angularjs';
import moment from 'moment';

import { LogoutDirectiveModule } from 'directives/logout.directive';
import { MyAccountServiceModule } from 'services/data/myAccount.service';
import { PlansService } from 'services/data/plans.service';
import { SubscriptionServiceModule, isDowngrade } from 'services/access/subscription/subscription.service';
import { TrialServiceModule, PARKED, EXPIRED } from 'components/trial/trial.service';
import { CheckoutComponentModule } from 'components/checkout/checkout.component';
import { CheckoutServiceModule } from 'components/checkout/checkout.service';
import { AppServiceModule } from 'app/app.service';
import { MobileAnalyticsServiceModule } from 'services/tracking/mobileApps/mobileAnalytics.service';

import { SelectPlanModule } from './selectPlan/selectPlan';
import template from './changePlan.html';
import './changePlan.scss';

const steps = {
    plans: 1,
    checkout: 2
};

export const ChangePlanModule = angular
    .module('app.components.changePlan', [
        'ngMaterial',
        uiRouter,
        MyAccountServiceModule,
        SubscriptionServiceModule,
        TrialServiceModule,
        CheckoutComponentModule,
        CheckoutServiceModule,
        PlansService,
        AppServiceModule,
        SelectPlanModule,
        MobileAnalyticsServiceModule,
        LogoutDirectiveModule
    ])
    .component('changePlan', {
        template,
        bindings: {
            isCloseAllowed: '<',
            showLogoutButton: '<',
            trialDaysRemaining: '<',
            selectPlanNote: '<',
            hideGreeting: '<',
            isModal: '<'
        },
        controller: class ChangePlan {
            constructor(
                $mdDialog,
                $state,
                $mdMedia,
                $scope,
                plansService,
                myAccountService,
                subscriptionsService,
                trialService,
                $q,
                checkoutService,
                mobileAnalyticsService,
                appService
            ) {
                'ngInject';

                angular.extend(this, {
                    cart: {},
                    steps,
                    step: steps.plans,
                    planUiDataB: [
                        { id: process.env.LIST_PERSONAL_PLAN_2_ID, icon: 'add' },
                        { id: process.env.LIST_PROFESSIONAL_PLAN_2_ID, icon: 'add' },
                        { id: process.env.STARTUP_PLAN_ID, icon: 'add', highlight: true },
                        { id: process.env.APRIL_BUSINESS_PLAN_ID, icon: 'add' }
                    ],
                    planUiData: [
                        { id: process.env.LIST_PERSONAL_PLAN_2_ID, icon: 'add' },
                        { id: process.env.LIST_PROFESSIONAL_PLAN_2_ID, icon: 'add' },
                        { id: process.env.STARTUP_PLAN_ID, icon: 'add', highlight: true },
                        { id: process.env.APRIL_BUSINESS_PLAN_ID, icon: 'add' }
                    ],
                    showAlternate: false,
                    $mdDialog,
                    $state,
                    $mdMedia,
                    $scope,
                    plansService,
                    myAccountService,
                    subscriptionsService,
                    trialService,
                    checkoutService,
                    appService,
                    mobileAnalyticsService,
                    $q
                });
            }

            $onInit() {
                this.getStepOneData();
                if (angular.isUndefined(this.isCloseAllowed)) {
                    this.isCloseAllowed = true;
                }
                if (angular.isUndefined(this.showLogoutButton)) {
                    this.showLogoutButton = false;
                }
                this.$scope.$watch(() => this.$mdMedia('gt-sm'), (gtSm) => {
                    this.planSortExpression = gtSm ? '' : 'highlight';
                });
                this.mobileAnalyticsService.trackEvent('Plans', {});
                this.onBackClick = this.goToStep.bind(this, 1);

                if (this.isModal === undefined) {
                    this.isModal = true;
                }
            }

            getStepOneData() {
                let offerPlans = this.planUiData;
                if (angular.element('body').hasClass('data-plans--alternate')) {
                    this.showAlternate = true;
                }
                this.$q.all([
                    this.plansService.getPlans(),
                    this.subscriptionsService.getSubscription(),
                    this.myAccountService.getUser()
                ]).then(([plans, subscription, user]) => {
                    this.user = user;
                    this.allPlans = plans;
                    this.cart.billingPeriodEnd = moment.unix(subscription.current_period_end).format('MMMM Do');
                    this.currentPlan = subscription.plan;

                    const startupBusinessPlanIds = [
                        process.env.STARTUP_PLAN_ID,
                        process.env.APRIL_BUSINESS_PLAN_ID,
                        process.env.STARTUP_3M_PLAN_ID,
                        process.env.BUSINESS_3M_PLAN_ID
                    ];

                    if (includes(startupBusinessPlanIds, this.currentPlan.id)) {
                        offerPlans = this.planUiDataB;
                    }

                    this.annotatePlans();
                    const hasAlternate = this.setUpAlternatesProperty();

                    this.currentPlans = this.mergeObjectArraysById(offerPlans, plans);
                    this.currentPlans.forEach((plan) => {
                        if (plan.id === subscription.plan.id) {
                            plan.isCurrentPlan = true;
                        }

                        if (hasAlternate[plan.id]) {
                            plan.alternates = hasAlternate[plan.id]
                                .map(planId => find(this.allPlans, { id: planId }));
                        }
                    });

                    return this.trialService.getTrialStatusFromSubscription(subscription).then((trialStatus) => {
                        this.isTrial = this.trialService.isValidTrialPlanStatus(trialStatus);
                        this.showCouponCodeField = this.isTrial || trialStatus === PARKED || trialStatus === EXPIRED;
                    });
                }).catch(() => {
                    this.close();
                });
            }

            annotatePlans() {
                const threeMonthPlanIds = [
                    process.env.LIST_PERSONAL_PLAN_2_ID_3M,
                    process.env.LIST_PROFESSIONAL_PLAN_2_ID_3M,
                    process.env.LIST_BUSINESS_PLAN_2_ID_3M,
                    process.env.STARTUP_3M_PLAN_ID,
                    process.env.BUSINESS_3M_PLAN_ID
                ];

                this.allPlans.forEach((plan) => {
                    if (threeMonthPlanIds.includes(plan.id)) {
                        Object.assign(plan, {
                            planType: '3-month prepaid',
                            displayAmount: plan.amount / plan.interval_count,
                            displayFirst: true,
                            pricePeriodLabel: 'billed quarterly'
                        });
                    } else {
                        plan.planType = 'Month to month';
                        plan.displayAmount = plan.amount;
                    }

                    // show/hide selection menu for pricing test
                    plan.showMenu = !this.showAlternate || (
                        plan.id !== process.env.LIST_PERSONAL_PLAN_2_ID &&
                        plan.id !== process.env.LIST_PERSONAL_PLAN_2_ID_3M
                    );
                });
            }

            setUpAlternatesProperty() {
                const hasAlternate = {};
                hasAlternate[process.env.LIST_PERSONAL_PLAN_2_ID] = [process.env.APRIL_BUSINESS_PLAN_ID];

                // original plans
                const originalPlans = this.planUiData.map(plan => plan.id);
                const originalPlansAlternates = [
                    [process.env.LIST_PERSONAL_PLAN_2_ID_3M],
                    [process.env.LIST_PROFESSIONAL_PLAN_2_ID_3M],
                    [process.env.LIST_BUSINESS_PLAN_2_ID_3M]
                ];

                originalPlans.forEach((plan, index) => {
                    hasAlternate[plan] = originalPlansAlternates[index];
                });

                // alternate plans
                const alternatePlans = this.planUiDataB.map(plan => plan.id);
                const alternatePlansAlternates = [
                    [process.env.LIST_PERSONAL_PLAN_2_ID_3M],
                    [process.env.LIST_PROFESSIONAL_PLAN_2_ID_3M],
                    [process.env.STARTUP_3M_PLAN_ID],
                    [process.env.BUSINESS_3M_PLAN_ID]
                ];

                alternatePlans.forEach((plan, index) => {
                    hasAlternate[plan] = alternatePlansAlternates[index];
                });

                return hasAlternate;
            }

            mergeObjectArraysById(arr1, arr2) {
                return map(arr1, (item) => {
                    return assign(item, find(arr2, { id: item.id }));
                });
            }

            close() {
                if (this.purchaseComplete) {
                    this.finish();
                } else {
                    this.$mdDialog.hide();
                }
            }

            finish() {
                this.appService.initLogging();
                this.$mdDialog.hide();
                this.$state.reload();
            }

            selectPlan(plan) {
                this.checkoutService.logProductClick(plan);
                this.checkoutService.logProductDetails(plan);

                if (plan.onPlanSelected) {
                    return plan.onPlanSelected();
                }

                this.cart.planDetails = plan;
                this.cart.planId = plan.id;
                this.cart.isDowngrade = isDowngrade(this.currentPlan, plan);

                this.trackPlan(plan.name);
                this.goToStep(2);
            }

            trackPlan(planName) {
                if (planName.length > 0) {
                    this.mobileAnalyticsService.trackEvent(`Plans:${planName}`, {});
                }
            }

            goToStep(stepNumber) {
                this.step = stepNumber;
            }

            onPurchaseComplete() {
                this.purchaseComplete = true;
            }
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/components/changePlan/changePlan.module.component.js